import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';

import type { UsageByClient } from '@/types/service/internalAnalytics';

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';

import { CHART_COLORS, formatCurrency, formatNumber } from '@/utils/saasDashboardHelpers';

interface LynAIUsageByClientChartProps {
  data: UsageByClient[];
}

interface CustomTooltipProps {
  active?: boolean;
  payload?: Array<{
    payload: {
      name: string;
      cost: number;
      tokens: number;
      inputTokens: number;
      outputTokens: number;
      users: number;
    };
  }>;
  label?: string;
}

const CustomTooltip = ({ active, payload, label }: CustomTooltipProps) => {
  if (!active || !payload?.length) return null;
  const data = payload[0].payload;
  return (
    <div className='bg-white p-3 border rounded shadow-lg'>
      <p className='font-medium text-sm mb-2'>{label}</p>
      <p className='text-sm'>Cost: {formatCurrency(data.cost)}</p>
      <p className='text-sm'>Total Tokens: {formatNumber(data.tokens)}</p>
      <p className='text-sm text-muted-foreground'>
        Input: {formatNumber(data.inputTokens)} / Output: {formatNumber(data.outputTokens)}
      </p>
      <p className='text-sm'>Users: {data.users}</p>
    </div>
  );
};

const LynAIUsageByClientChart = ({ data }: LynAIUsageByClientChartProps) => {
  if (data.length === 0) {
    return (
      <Card className='h-full'>
        <CardHeader>
          <CardTitle className='text-lg'>Cost by Client</CardTitle>
        </CardHeader>
        <CardContent>
          <div className='h-[300px] flex items-center justify-center text-muted-foreground'>
            No data available
          </div>
        </CardContent>
      </Card>
    );
  }

  // Transform data for chart - combine client name and instance
  const chartData = data
    .map((item) => ({
      name: `${item.client_name} (${item.client_instance})`,
      cost: parseFloat(item.total_cost_usd),
      tokens: item.total_tokens,
      inputTokens: item.total_input_tokens,
      outputTokens: item.total_output_tokens,
      users: item.unique_users,
    }))
    .slice(0, 15); // Limit to top 15

  // Calculate dynamic height (50px per item, minimum 300px)
  const chartHeight = Math.max(300, chartData.length * 50);

  // Calculate dynamic Y-axis width based on longest label
  const maxLabelLength = Math.max(...chartData.map((item) => item.name.length));
  const yAxisWidth = Math.min(Math.max(maxLabelLength * 7, 120), 250);

  return (
    <Card className='h-full'>
      <CardHeader>
        <CardTitle className='text-lg'>Cost by Client</CardTitle>
      </CardHeader>
      <CardContent>
        <ResponsiveContainer width='100%' height={chartHeight}>
          <BarChart
            data={chartData}
            layout='vertical'
            margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
          >
            <CartesianGrid strokeDasharray='3 3' horizontal={true} vertical={false} />
            <XAxis type='number' tickFormatter={(value) => formatCurrency(value)} />
            <YAxis type='category' dataKey='name' width={yAxisWidth} tick={{ fontSize: 11 }} />
            <Tooltip content={<CustomTooltip />} />
            <Bar
              dataKey='cost'
              name='Cost (USD)'
              fill={CHART_COLORS.pie[0]}
              radius={[0, 4, 4, 0]}
            />
          </BarChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  );
};

export default LynAIUsageByClientChart;
